import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Select, 
  message,
  Tabs,
  Progress,
  Tag
} from 'antd';
import { 
  CalendarOutlined, 
  UserOutlined,
  DownloadOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';

dayjs.locale('vi');

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

interface DailyReport {
  SessionDate: string;
  SessionTime: string;
  Location: string;
  StartTime: string;
  Status: string;
  TotalPresent: number;
  OpenedAt: string;
  ClosedAt: string;
}

interface WeeklyReport {
  WeekNumber: number;
  Year: number;
  Location: string;
  TotalSessions: number;
  TotalAttendances: number;
  UniqueAttendees: number;
}

interface MonthlyReport {
  Year: number;
  Month: number;
  Location: string;
  TotalSessions: number;
  TotalAttendances: number;
  UniqueAttendees: number;
}

const DailyAttendanceReportsPage: React.FC = () => {
  const [dailyData, setDailyData] = useState<DailyReport[]>([]);
  const [weeklyData, setWeeklyData] = useState<WeeklyReport[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('daily');

  useEffect(() => {
    fetchReports();
  }, [dateRange, locationFilter, activeTab]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        location: locationFilter
      });

      const response = await fetch(`/api/v1/daily-attendance/reports/${activeTab}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        }
      });

      const result = await response.json();
      
      if (result.success) {
        if (activeTab === 'daily') {
          setDailyData(result.data || []);
        } else if (activeTab === 'weekly') {
          setWeeklyData(result.data || []);
        } else if (activeTab === 'monthly') {
          setMonthlyData(result.data || []);
        }
      } else {
        message.error(result.error || 'Lỗi khi tải báo cáo');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      message.error('Lỗi kết nối đến server');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        location: locationFilter,
        type: activeTab
      });

      const response = await fetch(`/api/v1/daily-attendance/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `daily-attendance-report-${activeTab}-${dayjs().format('YYYY-MM-DD')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success('Xuất Excel thành công');
      } else {
        message.error('Lỗi khi xuất Excel');
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Lỗi khi xuất Excel');
    }
  };

  const getLocationName = (location: string) => {
    switch (location) {
      case 'hospital': return 'Bệnh viện';
      case 'school': return 'Trường';
      default: return location;
    }
  };

  const getSessionTimeName = (time: string) => {
    switch (time) {
      case 'morning_start': return 'Đầu giờ sáng';
      case 'morning_end': return 'Cuối giờ sáng';
      case 'afternoon_start': return 'Đầu giờ chiều';
      case 'afternoon_end': return 'Cuối giờ chiều';
      default: return time;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'processing';
      case 'closed': return 'success';
      case 'auto_closed': return 'warning';
      default: return 'default';
    }
  };

  // Calculate statistics
  const totalSessions = dailyData.length;
  const totalAttendances = dailyData.reduce((sum, d) => sum + d.TotalPresent, 0);
  const avgAttendance = totalSessions > 0 ? Math.round(totalAttendances / totalSessions) : 0;
  const openSessions = dailyData.filter(d => d.Status === 'open').length;

  const dailyColumns = [
    {
      title: 'Ngày',
      dataIndex: 'SessionDate',
      key: 'SessionDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Buổi',
      dataIndex: 'SessionTime',
      key: 'SessionTime',
      render: (time: string) => getSessionTimeName(time),
    },
    {
      title: 'Địa điểm',
      dataIndex: 'Location',
      key: 'Location',
      render: (location: string) => getLocationName(location),
    },
    {
      title: 'Thời gian',
      dataIndex: 'StartTime',
      key: 'StartTime',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status === 'open' ? 'Đang mở' : 
           status === 'closed' ? 'Đã đóng' : 
           status === 'auto_closed' ? 'Tự động đóng' : status}
        </Tag>
      ),
    },
    {
      title: 'Số người điểm danh',
      dataIndex: 'TotalPresent',
      key: 'TotalPresent',
      render: (count: number) => (
        <div>
          <Text strong>{count}</Text>
          <Progress 
            percent={Math.min((count / 100) * 100, 100)} 
            size="small" 
            showInfo={false}
            style={{ marginTop: 4 }}
          />
        </div>
      ),
    },
  ];

  const weeklyColumns = [
    {
      title: 'Tuần',
      key: 'week',
      render: (record: WeeklyReport) => `Tuần ${record.WeekNumber}/${record.Year}`,
    },
    {
      title: 'Địa điểm',
      dataIndex: 'Location',
      key: 'Location',
      render: (location: string) => getLocationName(location),
    },
    {
      title: 'Tổng phiên',
      dataIndex: 'TotalSessions',
      key: 'TotalSessions',
    },
    {
      title: 'Tổng lượt điểm danh',
      dataIndex: 'TotalAttendances',
      key: 'TotalAttendances',
    },
    {
      title: 'Số người duy nhất',
      dataIndex: 'UniqueAttendees',
      key: 'UniqueAttendees',
    },
    {
      title: 'Trung bình/phiên',
      key: 'avg',
      render: (record: WeeklyReport) => 
        record.TotalSessions > 0 ? Math.round(record.TotalAttendances / record.TotalSessions) : 0,
    },
  ];

  const monthlyColumns = [
    {
      title: 'Tháng',
      key: 'month',
      render: (record: MonthlyReport) => `${record.Month}/${record.Year}`,
    },
    {
      title: 'Địa điểm',
      dataIndex: 'Location',
      key: 'Location',
      render: (location: string) => getLocationName(location),
    },
    {
      title: 'Tổng phiên',
      dataIndex: 'TotalSessions',
      key: 'TotalSessions',
    },
    {
      title: 'Tổng lượt điểm danh',
      dataIndex: 'TotalAttendances',
      key: 'TotalAttendances',
    },
    {
      title: 'Số người duy nhất',
      dataIndex: 'UniqueAttendees',
      key: 'UniqueAttendees',
    },
    {
      title: 'Trung bình/phiên',
      key: 'avg',
      render: (record: MonthlyReport) => 
        record.TotalSessions > 0 ? Math.round(record.TotalAttendances / record.TotalSessions) : 0,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Báo cáo điểm danh cán bộ</Title>
        <Text type="secondary">
          Thống kê và phân tích dữ liệu điểm danh theo ngày, tuần, tháng
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tổng phiên"
              value={totalSessions}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tổng lượt điểm danh"
              value={totalAttendances}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Trung bình/phiên"
              value={avgAttendance}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Đang mở"
              value={openSessions}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong>Lọc theo:</Text>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col>
            <Select
              value={locationFilter}
              onChange={setLocationFilter}
              style={{ width: 120 }}
            >
              <Option value="all">Tất cả địa điểm</Option>
              <Option value="hospital">Bệnh viện</Option>
              <Option value="school">Trường</Option>
            </Select>
          </Col>
          <Col>
            <Button 
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
            >
              Xuất Excel
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Reports Tabs */}
      <Card>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'daily',
              label: (
                <span>
                  <CalendarOutlined />
                  Báo cáo ngày
                </span>
              ),
            },
            {
              key: 'weekly',
              label: (
                <span>
                  <BarChartOutlined />
                  Báo cáo tuần
                </span>
              ),
            },
            {
              key: 'monthly',
              label: (
                <span>
                  <PieChartOutlined />
                  Báo cáo tháng
                </span>
              ),
            },
          ]}
        >
          <TabPane tab="Báo cáo ngày" key="daily">
            <Table
              columns={dailyColumns}
              dataSource={dailyData}
              rowKey={(record) => `${record.SessionDate}-${record.SessionTime}-${record.Location}`}
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} của ${total} phiên`,
              }}
              scroll={{ x: 800 }}
            />
          </TabPane>

          <TabPane tab="Báo cáo tuần" key="weekly">
            <Table
              columns={weeklyColumns}
              dataSource={weeklyData}
              rowKey={(record) => `${record.Year}-${record.WeekNumber}-${record.Location}`}
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} của ${total} tuần`,
              }}
            />
          </TabPane>

          <TabPane tab="Báo cáo tháng" key="monthly">
            <Table
              columns={monthlyColumns}
              dataSource={monthlyData}
              rowKey={(record) => `${record.Year}-${record.Month}-${record.Location}`}
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} của ${total} tháng`,
              }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default DailyAttendanceReportsPage;
